import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ImageUploaderProps {
  images: File[];
  existingImageUrls?: string[];
  onChange: (images: File[]) => void;
  onRemoveExisting?: (url: string) => void;
}

export default function ImageUploader({
  images,
  existingImageUrls = [],
  onChange,
  onRemoveExisting,
}: ImageUploaderProps) {
  const [dragging, setDragging] = useState(false);
  const [pasteHint, setPasteHint] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const addFiles = useCallback(
    (files: FileList | File[] | null) => {
      if (!files) return;
      const valid = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (valid.length > 0) onChange([...images, ...valid]);
    },
    [images, onChange]
  );

  // Global paste handler — works anywhere on the page (Ctrl+V / Snipping Tool)
  useEffect(() => {
    const handlePaste = (e: Event) => {
      const items = (e as ClipboardEvent).clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            // Give it a readable name with timestamp
            const ext = file.type.split('/')[1] || 'png';
            const named = new File([file], `screenshot-${Date.now()}.${ext}`, { type: file.type });
            imageFiles.push(named);
          }
        }
      }

      if (imageFiles.length > 0) {
        onChange([...images, ...imageFiles]);
        // Flash the hint
        setPasteHint(true);
        setTimeout(() => setPasteHint(false), 2000);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [images, onChange]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const removeNew = (index: number) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div className="image-uploader" ref={containerRef}>
      <div
        className={`drop-zone ${dragging ? 'dragging' : ''} ${pasteHint ? 'paste-success' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {pasteHint ? (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon paste-success-icon">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p className="drop-zone-text" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
              Screenshot pasted!
            </p>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon">
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
            </svg>
            <p className="drop-zone-text">
              Drag &amp; drop,{' '}
              <label className="file-browse-label">
                browse
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileInput}
                  className="file-input-hidden"
                />
              </label>
              , or <strong>Ctrl+V</strong> to paste a screenshot
            </p>
            <p className="drop-zone-hint">PNG, JPG, GIF, WEBP · Snipping Tool &amp; clipboard supported</p>
          </>
        )}
      </div>

      {(existingImageUrls.length > 0 || images.length > 0) && (
        <div className="image-preview-grid">
          {existingImageUrls.map((url) => (
            <div key={url} className="image-preview-item">
              <img src={url} alt="Issue attachment" className="preview-img" />
              {onRemoveExisting && (
                <button
                  type="button"
                  className="preview-remove-btn"
                  onClick={() => onRemoveExisting(url)}
                  title="Remove image"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          {images.map((file, idx) => (
            <div key={idx} className="image-preview-item">
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="preview-img"
              />
              <button
                type="button"
                className="preview-remove-btn"
                onClick={() => removeNew(idx)}
                title="Remove image"
              >
                ×
              </button>
              <span className="preview-new-badge">
                {file.name.startsWith('screenshot-') ? '📋 Pasted' : 'New'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
